import { UserIdSchema } from "@core/domains/identity";
import { identity } from "@core/foundation/function";
import { ulid } from "@core/foundation/id";
import {
  createModel,
  createModelId,
  createModelSchemas,
} from "@core/foundation/model";
import { z } from "@core/zod";

import { ID_PREFIX } from "./shared/id-prefix.ts";

const BalanceProviderSchema = z.enum(["admin", "stripe"]);

const BalanceTypeSchema = z.enum(["monthly", "topUp"]);

export type BalanceType = z.infer<typeof BalanceTypeSchema>;

const { generateId: generateBalanceId, idSchema: BalanceIdSchema } =
  createModelId({ digestFn: ulid, idPrefix: ID_PREFIX.BALANCE });

export { BalanceIdSchema };

export const Balance = createModel({
  create: (item, schema) => {
    return {
      ...schema.decode(item),
      amount: item.initialAmount,
      etag: null,
      id: generateBalanceId(),
    };
  },
  schemas: createModelSchemas({
    base: z.object(
      {
        amount: z.decimal().nonnegative(),
        description: z.string().optional(),
        etag: z.string().nullable(),
        expiresAt: z.iso.datetime().optional(),
        id: BalanceIdSchema,
        initialAmount: z.decimal().positive(),
        principalId: UserIdSchema,
        provider: BalanceProviderSchema,
        type: BalanceTypeSchema,
      },
      {
        amount: "The credit the balance holds now, as a decimal string.",
        description: "The note recorded with the balance.",
        etag: "The balance's version, which storage stamps for concurrency.",
        expiresAt: "When the balance expires, as an ISO 8601 timestamp.",
        id: "The unique identifier for the balance.",
        initialAmount:
          "The credit the balance was granted, as a decimal string.",
        principalId: "The id of the user the balance belongs to.",
        provider: "Who granted the balance, either admin or stripe.",
        type: "The balance's type, either monthly or topUp.",
      },
    ),
    derive: {
      create: schema => schema.omit({ amount: true, etag: true, id: true }),
      recovery: identity,
      update: schema => schema.pick({ amount: true }).partial(),
    },
  }),
  update: (item, update, schema) => {
    return { ...item, ...schema.decode(update) };
  },
});

export interface Balance extends z.infer<typeof Balance.schema> {}

export interface BalanceCreate extends z.input<typeof Balance.createSchema> {}
